/* Package imports */
import React, { Suspense } from "react";
import { Navigate } from "react-router-dom";
import { useRoutes } from "react-router-dom";
import PrivateRoute from "./PrivateRoute";

/* Utility imports */
import { GlobalValues } from "../constants/global";

/* Import layouts/containers */
import DefaultLayout from "../layouts/Default";
import VerticalLayout from "../layouts/Vertical";

/* Import root component */
import Root from "./Root";

/* Lazy load all views */
// Error views
const PageNotFound = React.lazy(() => import("../pages/PageNotFound"));
// Auth views
const Login = React.lazy(() => import("../pages/account/Login"));
const VerifyOtp = React.lazy(() => import("../pages/account/VerifyOtp"));
const Logout = React.lazy(() => import("../pages/account/Logout"));
// Dashboard
const Dashboard = React.lazy(() => import("../pages/dashboard/index"));
// Visitor

const ViewTermsAndConditionsManage = React.lazy(() => import("../pages/ViewTermsAndConditionsManage"));

/* Super Admin */
const ManageOrganization = React.lazy(() => import("../pages/superAdmin/organization/ManageOrganization"));
const ListOrganization = React.lazy(() => import("../pages/superAdmin/organization/ListOrganization"));
const AssignOrganizationZones = React.lazy(() => import("../pages/superAdmin/organization/AssignOrganizationZones"));
const AssignOrganizationCategories = React.lazy(() => import("../pages/superAdmin/organization/AssignOrganizationCategories"));
const ListOrganizationZones = React.lazy(() => import("../pages/superAdmin/organization/ListOrganizationZones"));

const ListOrganizationUser = React.lazy(() => import("../pages/superAdmin/user/ListOrganizationUser"));
const ManageOrganizationUser = React.lazy(() => import("../pages/superAdmin/user/ManageOrganizationUser"));

const ListServiceCategory = React.lazy(() => import("../pages/superAdmin/serviceCategory/ListServiceCategory"));
const ManageServiceCategory = React.lazy(() => import("../pages/superAdmin/serviceCategory/ManageServiceCategory"));
const ReorderServiceCategory = React.lazy(() => import("../pages/superAdmin/serviceCategory/ReorderServiceCategory"));

const ListServiceSubCategory = React.lazy(() => import("../pages/superAdmin/serviceSubCategory/ListServiceSubCategory"));
const ManageServiceSubCategory = React.lazy(() => import("../pages/superAdmin/serviceSubCategory/ManageServiceSubCategory"));
const ReorderServiceSubCategory = React.lazy(() => import("../pages/superAdmin/serviceSubCategory/ReorderServiceSubCategory"));

const ListService = React.lazy(() => import("../pages/superAdmin/service/ListService"));
const ManageService = React.lazy(() => import("../pages/superAdmin/service/ManageService"));
const ReorderService = React.lazy(() => import("../pages/superAdmin/service/ReorderService"));
const ManageAgreement = React.lazy(() => import("../pages/superAdmin/agreement/ManageAgreement"));

const ListState = React.lazy(() => import("../pages/superAdmin/state/ListState"));
const ManageState = React.lazy(() => import("../pages/superAdmin/state/ManageState"));
const ListCity = React.lazy(() => import("../pages/superAdmin/city/ListCity"));
const ManageCity = React.lazy(() => import("../pages/superAdmin/city/ManageCity"));
const ListZone = React.lazy(() => import("../pages/superAdmin/zone/ListZone"));
const ManageZone = React.lazy(() => import("../pages/superAdmin/zone/ManageZone"));
const AssignZonePincode = React.lazy(() => import("../pages/superAdmin/zone/AssignZonePincode"));
const ListZonePincode = React.lazy(() => import("../pages/superAdmin/zone/ListZonePincode"));
const ManageZonePincode = React.lazy(() => import("../pages/superAdmin/zone/ManageZonePincode"));
const ListCrmUser = React.lazy(() => import("../pages/superAdmin/crm/ListCrmUser"));
const ListAggregatorAssociatesForPartner = React.lazy(() => import("../pages/superAdmin/organization/ListAggregatorAssociatesForPartner"));
const CustomerAnalyticsShell = React.lazy(() => import("../pages/superAdmin/customerAnalytics/CustomerAnalyticsShell"));
const CustomerAnalyticsIndex = React.lazy(() => import("../pages/superAdmin/customerAnalytics/CustomerAnalyticsIndex"));
const RegisteredUtilizedCustomers = React.lazy(() => import("../pages/superAdmin/customerAnalytics/RegisteredUtilizedCustomers"));
const DeletedCustomers = React.lazy(() => import("../pages/superAdmin/customerAnalytics/DeletedCustomers"));
const RepeatCustomers = React.lazy(() => import("../pages/superAdmin/customerAnalytics/RepeatCustomers"));

/* Aggregator Admin Reports */
const BookingsRevenueSummary = React.lazy(() => import("../pages/superAdmin/serviceBooking/BookingsRevenueSummary"));
const RevenueBifurcationSummary = React.lazy(() => import("../pages/superAdmin/serviceBooking/RevenueBifurcationSummary"));
const AggregatorAdminAllBookingOnline = React.lazy(() => import("../pages/superAdmin/serviceBooking/AggregatorAdminAllBookingOnline"));
const AggregatorAdminAllBookingCrm = React.lazy(() => import("../pages/superAdmin/serviceBooking/AggregatorAdminAllBookingCrm"));

/* Customer */
const PincodeServiceabilityCheck = React.lazy(() => import("../pages/customer/PincodeServiceabilityCheck"));

/* CRM */
const ManageCrmServiceRequest = React.lazy(() => import("../pages/crm/ManageCrmServiceRequest"));
const ListServiceRequestView = React.lazy(() => import("../pages/crm/ListServiceRequestView"));
const ManageServiceRequestView = React.lazy(() => import("../pages/crm/ManageServiceRequestView"));
const ManageAllBookingsView = React.lazy(() => import("../pages/crm/ManageAllBookingsView"));
const ListOnlineBookingsView = React.lazy(() => import("../pages/crm/ListOnlineBookingsView"));
const ManageOnlineBookingsView = React.lazy(() => import("../pages/crm/ManageOnlineBookingsView"));
const ListCrmBookingsView = React.lazy(() => import("../pages/crm/ListCrmBookingsView"));

/* Organization Admin */
const ListUser = React.lazy(() => import("../pages/organizationAdmin/user/ListUser"));
const ManageUser = React.lazy(() => import("../pages/organizationAdmin/user/ManageUser"));
const AssociatePerformance = React.lazy(() => import("../pages/organizationAdmin/user/AssociatePerformance"));
const ListRoster = React.lazy(() => import("../pages/organizationAdmin/roster/ListRoster"));
const ManageRoster = React.lazy(() => import("../pages/organizationAdmin/roster/ManageRoster"));
const BulkUploadRoster = React.lazy(() => import("../pages/organizationAdmin/roster/BulkUploadRoster"));
const AssociateAvailabilityCalendar = React.lazy(() => import("../pages/organizationAdmin/roster/AssociateAvailabilityCalendar"));
const ManageCrmUser = React.lazy(() => import("../pages/organizationAdmin/crm/ManageCrmUser"));

const ListServiceRequest = React.lazy(() => import("../pages/organizationAdmin/serviceRequest/ListServiceRequest"));
const ManageServiceRequest = React.lazy(() => import("../pages/organizationAdmin/serviceRequest/ManageServiceRequest"));
const ListCrmServiceRequest = React.lazy(() => import("../pages/organizationAdmin/serviceRequest/ListCrmServiceRequest"));
const ListForwardedServiceRequestInternal = React.lazy(() => import("../pages/organizationAdmin/serviceRequest/ListForwardedServiceRequestInternal"));
const ListForwardedCrmServiceRequestInternal = React.lazy(() => import("../pages/organizationAdmin/serviceRequest/ListForwardedCrmServiceRequestInternal"));
const CustomerFeedbackList = React.lazy(() => import("../pages/organizationAdmin/reports/CustomerFeedbackList"));

/* Define loading component (fallback to suspenseful loading) */
const loading = () => <div className=""></div>;

/* Utility method for loading component */
const LoadComponent = ({ component: Component }) => {
    return (
        <Suspense fallback={loading()}>
            <Component />
        </Suspense>
    );
};

/* Declare all app routes */
const AllRoutes = () => {
    let Layout = VerticalLayout;

    return useRoutes([
        /* Root component which navigates to the required URL */
        { path: "/", element: <Root /> },
        {
            /* Public route */
            path: "/",
            element: <DefaultLayout />,
            children: [
                {
                    path: "account",
                    children: [
                        { path: "login", element: <LoadComponent component={Login} /> },
                        { path: "verify-otp", element: <LoadComponent component={VerifyOtp} /> },
                        { path: "logout", element: <LoadComponent component={Logout} /> }
                    ]
                },
                {
                    path: "terms-and-conditions",
                    element: <LoadComponent component={ViewTermsAndConditionsManage} />
                }
            ]
        },
        {
            /* Protected route */
            path: "/",
            element: <PrivateRoute component={Layout} />,
            children: [
                {
                    path: "dashboard",
                    children: [
                        {
                            path: "home",
                            element: <LoadComponent component={Dashboard} />
                        }
                    ]
                }
            ]
        },
        {
            /* Protected route */
            path: "/",
            element: <PrivateRoute roles={[GlobalValues.aggregatorAdministratorRole.id]} component={Layout} />,
            children: [
                {
                    path: "organization",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListOrganization} />
                        },
                        {
                            path: "associates/list",
                            element: <LoadComponent component={ListAggregatorAssociatesForPartner} />
                        },
                        {
                            path: "zones",
                            element: <LoadComponent component={ListOrganizationZones} />
                        },
                        {
                            path: "manage",
                            element: <LoadComponent component={ManageOrganization} />
                        },
                        {
                            path: "assign-zones",
                            element: <LoadComponent component={AssignOrganizationZones} />
                        }
                        ,
                        {
                            path: "assign-categories",
                            element: <LoadComponent component={AssignOrganizationCategories} />
                        }
                    ]
                },
                {
                    path: "organization/user",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListOrganizationUser} />
                        },
                        {
                            path: "manage",
                            element: <LoadComponent component={ManageOrganizationUser} />
                        }
                    ]
                },
                {
                    path: "crm",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListCrmUser} />
                        },
                        {
                            path: "manage",
                            element: <LoadComponent component={ManageCrmUser} />
                        }
                    ]
                },
                {
                    path: "servicecategory",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListServiceCategory} />
                        },
                        {
                            path: "manage",
                            element: <LoadComponent component={ManageServiceCategory} />
                        },
                        {
                            path: "reorder",
                            element: <LoadComponent component={ReorderServiceCategory} />
                        }
                    ]
                },
                {
                    path: "servicesubcategory",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListServiceSubCategory} />
                        },
                        {
                            path: "manage",
                            element: <LoadComponent component={ManageServiceSubCategory} />
                        },
                        {
                            path: "reorder",
                            element: <LoadComponent component={ReorderServiceSubCategory} />
                        }
                    ]
                },
                {
                    path: "service",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListService} />
                        },
                        {
                            path: "manage",
                            element: <LoadComponent component={ManageService} />
                        },
                        {
                            path: "reorder",
                            element: <LoadComponent component={ReorderService} />
                        }
                    ]
                },
                {
                    path: "agreement",
                    children: [
                        {
                            path: "page",
                            element: <LoadComponent component={ManageAgreement} />
                        }
                    ]
                },
                {
                    path: "booking",
                    children: [
                        {
                            // backward-compatible route (old link/bookmark)
                            path: "list",
                            element: <Navigate to="/booking/all-booking/online" replace />
                        },
                        {
                            path: "all-booking/online",
                            element: <LoadComponent component={AggregatorAdminAllBookingOnline} />
                        },
                        {
                            path: "all-booking/crm",
                            element: <LoadComponent component={AggregatorAdminAllBookingCrm} />
                        },
                        {
                            path: "revenue-summary",
                            element: <LoadComponent component={BookingsRevenueSummary} />
                        },
                        {
                            path: "revenue-bifurcation-summary",
                            element: <LoadComponent component={RevenueBifurcationSummary} />
                        }
                    ]
                },
                {
                    path: "customer",
                    children: [
                        {
                            path: "analytics",
                            element: <LoadComponent component={CustomerAnalyticsShell} />,
                            children: [
                                {
                                    path: "",
                                    element: <LoadComponent component={CustomerAnalyticsIndex} />
                                },
                                {
                                    path: "registered-utilized",
                                    element: <LoadComponent component={RegisteredUtilizedCustomers} />
                                },
                                {
                                    path: "deleted-customers",
                                    element: <LoadComponent component={DeletedCustomers} />
                                },
                                {
                                    path: "repeat-customers",
                                    element: <LoadComponent component={RepeatCustomers} />
                                }
                            ]
                        }
                    ]
                },
                {
                    path: "zone",
                    children: [
                        {
                            path: "state/list",
                            element: <LoadComponent component={ListState} />
                        },
                        {
                            path: "state/manage",
                            element: <LoadComponent component={ManageState} />
                        },
                        {
                            path: "city/list",
                            element: <LoadComponent component={ListCity} />
                        },
                        {
                            path: "city/manage",
                            element: <LoadComponent component={ManageCity} />
                        },
                        {
                            path: "list",
                            element: <LoadComponent component={ListZone} />
                        },
                        {
                            path: "manage",
                            element: <LoadComponent component={ManageZone} />
                        },
                        {
                            path: "assign-pincode",
                            element: <LoadComponent component={AssignZonePincode} />
                        },
                        {
                            path: "pincode/list",
                            element: <LoadComponent component={ListZonePincode} />
                        },
                        {
                            path: "pincode/manage",
                            element: <LoadComponent component={ManageZonePincode} />
                        }
                    ]
                }
            ]
        },
        {
            /* Protected route */
            path: "/",
            element: <PrivateRoute roles={[GlobalValues.aggregatorPartnerAdministratorRole.id]} component={Layout} />,
            children: [
                {
                    path: "user",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListUser} />
                        },
                        {
                            path: "performance",
                            element: <LoadComponent component={AssociatePerformance} />
                        },
                        {
                            path: "manage",
                            element: <LoadComponent component={ManageUser} />
                        }
                    ]
                },
                {
                    path: "roster",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListRoster} />
                        },
                        {
                            path: "manage",
                            element: <LoadComponent component={ManageRoster} />
                        },
                        {
                            path: "bulk-upload",
                            element: <LoadComponent component={BulkUploadRoster} />
                        }
                    ]
                },
                {
                    path: "associate-availability",
                    element: <LoadComponent component={AssociateAvailabilityCalendar} />
                },
                {
                    path: "service/service-request/",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListServiceRequest} />
                        },
                        {
                            path: "manage",
                            element: <LoadComponent component={ManageServiceRequest} />
                        }
                    ]
                }
                ,
                {
                    path: "service/crm-service-request/",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListCrmServiceRequest} />
                        }
                    ]
                }
                ,
                {
                    path: "service/forwarded-service-request/",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListForwardedServiceRequestInternal} />
                        }
                    ]
                }
                ,
                {
                    path: "service/forwarded-crm-service-request/",
                    children: [
                        {
                            path: "list",
                            element: <LoadComponent component={ListForwardedCrmServiceRequestInternal} />
                        }
                    ]
                }
                ,
                {
                    path: "report",
                    children: [
                        {
                            path: "customer-feedback",
                            element: <LoadComponent component={CustomerFeedbackList} />
                        }
                    ]
                }
            ]
        },
        {
            /* Protected route */
            path: "/",
            element: <PrivateRoute roles={[GlobalValues.customerRole.id]} component={Layout} />,
            children: [
                {
                    path: "customer",
                    children: [
                        {
                            path: "pincode-serviceability",
                            element: <LoadComponent component={PincodeServiceabilityCheck} />
                        }
                    ]
                }
            ]
        },
        {
            /* Protected route */
            path: "/",
            element: <PrivateRoute roles={[GlobalValues.crmRole.id]} component={Layout} />,
            children: [
                {
                    path: "crm",
                    children: [
                        {
                            path: "bookings/online/list",
                            element: <LoadComponent component={ListOnlineBookingsView} />
                        },
                        {
                            path: "bookings/online/manage",
                            element: <LoadComponent component={ManageOnlineBookingsView} />
                        },
                        {
                            path: "bookings/list",
                            element: <LoadComponent component={ListCrmBookingsView} />
                        },
                        {
                            path: "bookings/manage",
                            element: <LoadComponent component={ManageAllBookingsView} />
                        },
                        {
                            path: "service-request/list",
                            element: <LoadComponent component={ListServiceRequestView} />
                        },
                        {
                            path: "service-request/manage",
                            element: <LoadComponent component={ManageServiceRequestView} />
                        },
                        {
                            path: "booking/manage",
                            element: <LoadComponent component={ManageCrmServiceRequest} />
                        }
                    ]
                }
            ]
        },
        /* Wildcard for page not found */
        { path: "*", element: <LoadComponent component={PageNotFound} /> }
    ]);
};

export { AllRoutes };
